import { fetchData, fetchCheckout } from '../utils/async';
import { addClass, removeClass } from '../utils/dom';
import { getRestrictedContent } from './environment';
import { deepCompare, deepClone } from '../utils/object';
import { fieldValidation, urlify } from '../utils/string';

export const SUBSCRIBED = 'SUBSCRIBED';
export const SUBSCRIBED_ERROR = 'SUBSCRIBED_ERROR';
export const RECENTLY_VIEWED = 'RECENTLY_VIEWED';
export const ADD_VOUCHER = 'ADD_VOUCHER';
export const VOUCHER_INPUT = 'VOUCHER_INPUT';
export const VOUCHER_ERROR = 'VOUCHER_ERROR';
export const ADDED_TO_BAG = 'ADDED_TO_BAG';
export const ADD_TO_BAG_ERROR = 'ADD_TO_BAG_ERROR';
export const DISCOUNT_ERROR = 'DISCOUNT_ERROR';
export const SELECT_SIZE = 'SELECT_SIZE';
export const SELECT_QUANTITY = 'SELECT_QUANTITY';
export const UPDATE_SELECTION_CACHE = 'UPDATE_SELECTION_CACHE';
export const UPDATE_SELECTION = 'UPDATE_SELECTION';
export const UPDATE_SELECTION_ERROR = 'UPDATE_SELECTION_ERROR';
export const UPDATE_ADDITIONAL_INFORMATION = 'UPDATE_ADDITIONAL_INFORMATION';
export const UPDATE_BUDBEE = 'UPDATE_BUDBEE';
export const GET_ADDRESS = 'GET_ADDRESS';
export const UPDATE_ADDRESS = 'UPDATE_ADDRESS';
export const UPDATE_ERRORS = 'UPDATE_ERRORS';
export const DELIVERY_PROCEED = 'DELIVERY_PROCEED';
export const KLARNA_LOADED = 'KLARNA_LOADED';
export const KLARNA_UNLOADED = 'KLARNA_UNLOADED';
export const ADYEN_INITIATED = 'ADYEN_INITIATED';
export const STRIPE_INITIATED = 'STRIPE_INITIATED';
export const PAYMENT_UPDATING = 'PAYMENT_UPDATING';
export const SELECTION_ERROR = 'SELECTION_ERROR';
export const SELECTION_ERROR_STOCK = 'SELECTION_ERROR_STOCK';
export const ENRICH_PRODUCTS = 'ENRICH_PRODUCTS';
export const INITIATED_SCRIPT = 'INITIATED_SCRIPT';
export const BOOLEAN_CHANGE = 'BOOLEAN_CHANGE';
export const PAYMENT_RETURN = 'PAYMENT_RETURN';
export const DESTROY_SELECTION = 'DESTROY_SELECTION';

export const ACCOUNT_LOGOUT = 'ACCOUNT_LOGOUT';
export const UPDATE_ACCESS_TOKEN = 'UPDATE_ACCESS_TOKEN';
export const UPDATE_CUSTOMER_DATA = 'UPDATE_CUSTOMER_DATA';
export const ACCOUNT_LOGIN_ERROR = 'ACCOUNT_LOGIN_ERROR';

export const clientInit = () => ({ type: 'CLIENT_INIT' });
export const retrieveProductCache = () => ({ type: 'RETRIEVE_PRODUCT_CACHE' });
export const selectSize = (product, item, quantity = 1) => ({ type: SELECT_SIZE, product, item, quantity });
export const addRecentlyViewed = (product) => ({ type: RECENTLY_VIEWED, product });

const removeInvalidPaymentFields = (setup, paymentData) => {
	const whitelistedFields = {
		address: ['address1', 'address2', 'city', 'company', 'country', 'firstName', 'lastName', 'phone', 'province', 'zip'],
	};

	// Copy only whitelisted fields.
	const returnData = Object.keys(paymentData).reduce((acc, f) => {
		// if (whitelistedFields.level0.indexOf(f) === -1) return acc;
		if (whitelistedFields[f]) {
			// check second level.
			return {
				...acc,
				[f]: Object.keys(paymentData[f]).reduce((acc1, f1) => {
					if (whitelistedFields[f].indexOf(f1) === -1) return acc1;
					return { ...acc1, [f1]: paymentData[f][f1] };
				}, {}),
			};
		}
		if (typeof paymentData[f] === 'object') return { ...acc, [f]: deepClone(paymentData[f]) };
		return { ...acc, [f]: paymentData[f] };
	}, {});

	// Add required fields
	returnData.address.country = returnData.address.country || setup.country;

	// Check that state is correct and applicable else remove.
	if (!(returnData.address.state && setup.countries?.[returnData.address.country]?.states?.[returnData.address.state])) delete returnData.address.state;

	return returnData;
};

export const subscribeMailChimp = ({ email, firstName, lastName, type, tags = [] }) => (dispatch, getState) => {
	const { country, language } = getState().environment.setup;

	let tag = 'WEBSITE SUBSCRIBERS';
	if (type === 'trade') tag = `${tag} PROFESSIONALS`;
	if (country === 'US') tag = `${tag} USA`;

	return (
		fetchData('mailchimp/subscribe', { email, firstName, lastName, tags: [tag, ...tags], country, language }).then((data) => {
			if (!data || data.errors) dispatch({ type: SUBSCRIBED_ERROR });
			return dispatch({ type: SUBSCRIBED });
		}).catch((err) => console.log(err))
	);
};

/* Create new checkout or add to checkout with email address. */
export const subscribeToNewsletter = ({ email, firstName, lastName, type, tags = [] }) => (dispatch, getState) => {
	const { market } = getState().environment.setup;
	const token = getState().checkout.selection.token;

	if (!token) {
		return dispatch(subscribeMailChimp({ email, firstName, lastName, type, tags })).then(() => dispatch(createCheckout({ email })));
	}

	const variables = {
		checkoutId: token,
		email,
	};

	return dispatch(subscribeMailChimp({ email, firstName, lastName, type, tags })).then(() => (
		fetchCheckout('checkoutEmailUpdate', market, variables)
	));
};

export const updateCheckoutAttributes = ({ market, token, customAttributes }) => {
	const variables = {
		checkoutId: token,
		input: {
			customAttributes,
		},
	};

	return fetchCheckout('checkoutAttributesUpdate', market, variables).then((response) => response.data.checkoutAttributesUpdateV2.checkout.id);
};

export const createCheckout = ({ email = false, lineItems = [], token = false, skipUpdate = false, addToBag = false }) => (dispatch, getState) => {
	const { country, market } = getState().environment.setup;
	const { items: itemsFromState = [], token: tokenFromState = false } = getState().checkout.selection;

	if (!token && tokenFromState) token = tokenFromState;

	if (token && !email && !skipUpdate) {
		const customAttributes = [...lineItems, ...itemsFromState]
			.filter((item) => item.customAttributes && item.customAttributes.length > 0)
			.reduce((acc, item) => ({
				...acc,
				...item.customAttributes.reduce((acc2, item2) => ({
					...acc2,
					[urlify(item2.key)]: item2,
				}), {}),
			}), {});

		return updateCheckoutAttributes({ market, token, customAttributes: Object.keys(customAttributes).map((attribute) => customAttributes[attribute]) });
	}

	if (token && lineItems.length === 0) {
		return Promise.resolve(token);
	}

	const variables = {
		input: {
			buyerIdentity: {
				countryCode: country,
			},
			customAttributes: lineItems
				.filter((lineItem) => lineItem.customAttributes && lineItem.customAttributes.length > 0)
				.reduce((acc, lineItem) => [...acc, ...lineItem.customAttributes], []),
		},
	};
	if (email) variables.input.email = email;
	if (lineItems.length > 0 && !addToBag) variables.input.lineItems = lineItems;

	return fetchCheckout('checkoutCreate', market, variables).then((response) => {
		dispatch({ type: UPDATE_SELECTION, data: response.data.checkoutCreate.checkout });
		return response.data.checkoutCreate.checkout.id;
	});
};

export const applyDiscountCode = ({ discountCode }) => (dispatch, getState) => {
	const { market } = getState().environment.setup;

	return (
		dispatch(createCheckout({ })).then((checkoutId) => fetchCheckout('checkoutDiscountCodeApplyV2', market, { checkoutId, discountCode })).then((response) => {
			if (response.data.checkoutDiscountCodeApplyV2.checkoutUserErrors.length > 0) {
				return dispatch({ type: DISCOUNT_ERROR });
			}

			return dispatch({ type: UPDATE_SELECTION, data: response.data.checkoutDiscountCodeApplyV2.checkout });
		})
	);
};

export const updateCheckoutAddress = () => (dispatch, getState) => {
	const token = getState().checkout.selection.token;
	const { payment } = getState().checkout;
	const { setup } = getState().environment;
	const { countries, market } = setup;

	const cleanData = removeInvalidPaymentFields(setup, payment);

	const addressVariables = {
		checkoutId: token,
		shippingAddress: Object.keys(cleanData.address).reduce((acc, f) => {
			let value = cleanData.address[f];
			if (f === 'country') value = countries[value].name;
			return {
				...acc,
				[f]: value,
			};
		}, {}),
	};

	return fetchCheckout('checkoutShippingAddressUpdate', market, addressVariables).then((response) => {
		dispatch({ type: UPDATE_SELECTION, data: response.data.checkoutShippingAddressUpdateV2.checkout });
		const { checkoutUserErrors = [] } = response.data.checkoutShippingAddressUpdateV2;

		if (checkoutUserErrors.length > 0) {
			const errors = [];
			checkoutUserErrors.forEach((checkoutUserError) => {
				errors[errors.length] = {
					group: checkoutUserError.field[0],
					id: checkoutUserError.field[1],
					message: checkoutUserError.message,
				};
			});

			return new Promise((resolve, reject) => reject({ checkoutUserErrors: errors }));
		}

		return response.data.checkoutShippingAddressUpdateV2.checkout.id;
	}).then((response) => {
		const emailVariables = {
			checkoutId: response,
			email: getState().checkout.payment.address.email,
		};

		return fetchCheckout('checkoutEmailUpdate', market, emailVariables);
	});
};

export const addToBag = (lineItems = [], cachedItems, category) => (dispatch, getState) => {
	const { market } = getState().environment.setup;
	addClass(document.querySelector('html'), 'u_pageIsScrollingUp');

	return (
		dispatch(createCheckout({ lineItems, addToBag: 1 })).then((checkoutId) => fetchCheckout('checkoutLineItemsAdd', market, { checkoutId, lineItems })).then((response) => {
			if (response.data.checkoutLineItemsAdd.checkoutUserErrors.length > 0) return dispatch({ type: ADD_TO_BAG_ERROR });
			dispatch({ type: UPDATE_SELECTION_CACHE, lineItems, cachedItem: cachedItems, category });

			setTimeout(() => {
				addClass(document.querySelector('html'), 'is_addedToCheckout');
				dispatch({ type: ADDED_TO_BAG, open: true });
			}, 50);

			setTimeout(() => {
				removeClass(document.querySelector('html'), 'is_addedToCheckout');
				dispatch({ type: ADDED_TO_BAG, open: false });
			}, 4000);

			return dispatch({ type: UPDATE_SELECTION, data: response.data.checkoutLineItemsAdd.checkout, addedToBag: true });
		}).catch((err) => console.log('DEBUG: err', err))
	);
};

export const changeQuantity = (lineItems = [], cachedItem, category) => (dispatch, getState) => {
	const { market } = getState().environment.setup;

	return dispatch(createCheckout({ })).then((checkoutId) => fetchCheckout('checkoutLineItemsUpdate', market, { checkoutId, lineItems })).then((response) => {
		dispatch({ type: UPDATE_SELECTION_CACHE, lineItems, cachedItem, category });

		return dispatch({ type: UPDATE_SELECTION, data: response.data.checkoutLineItemsUpdate.checkout });
	}).catch((err) => console.log('DEBUG: err', err));
};

export const updateCheckout = () => (dispatch, getState) => {
	const { items = [], token = false } = getState().checkout.selection;

	// If checkout never created then don't do anything.
	if (items.length === 0 && !token) return new Promise((resolve) => resolve());

	// Re-create a new basket with the current items.
	// TODO: Check that the items are available in current market.
	const lineItems = items.map((item) => ({ variantId: item.id, quantity: item.quantity, customAttributes: item.customAttributes }));

	return dispatch(createCheckout({ lineItems, token: 1, skipUpdate: true }));
};

export const changeAddressInput = (group, id, value) => ({ type: UPDATE_ADDRESS, group, id, value });
export const booleanChange = (id) => ({ type: BOOLEAN_CHANGE, id });

export const checkAddressInputErrors = (group, id, value) => {
	const fieldType = id === 'email' ? 'email' : 'text';
	const error = fieldValidation(value, fieldType);

	return ({ type: UPDATE_ERRORS, group, id, error });
};

export const enrichProducts = (params) => ({ type: ENRICH_PRODUCTS, ...params });

export const destroySelection = () => (dispatch, getState) => {
	const { country, countries } = getState().environment.setup;
	const defaultCountrySettings = countries[country];
	// Clear possible campaign market/pricelist to keep in sync with Centra.
	dispatch({ type: 'UPDATE_COUNTRY', country, market: parseInt(defaultCountrySettings.market, 10), pricelist: parseInt(defaultCountrySettings.pricelist, 10) });
	dispatch({ type: 'RETRIEVE_PRODUCT_CACHE' });

	dispatch({ type: DESTROY_SELECTION });
};

// ACCOUNT - Use only US market for intranet.
export const forgot = () => (dispatch, getState) => {
	const { email } = getState().checkout.payment.address;

	return (
		fetchCheckout('accountForgot', 'account', { email })
			.then((data) => (data.errors ? new Promise((resolve, reject) => reject()) : data))
			.then(() => dispatch({ type: 'ACCOUNT_FORGOT_SENT' }))
			.catch(() => dispatch({ type: 'ACCOUNT_FORGOT_FAILED', hasError: true }))
	);
};

export const activate = () => (dispatch, getState) => {
	const { customerId = false, activationToken = false } = getState().location.payload;
	const { passwordNewRepeat } = getState().checkout.payment.address;

	const activationVariables = {
		id: `gid://shopify/Customer/${customerId}`,
		input: {
			activationToken,
			password: passwordNewRepeat,
		},
	};

	return fetchCheckout('accountActivate', 'account', activationVariables).then((response) => {
		dispatch({ type: UPDATE_ACCESS_TOKEN, data: response.data.customerActivate.customerAccessToken });
		dispatch({ type: UPDATE_CUSTOMER_DATA, data: response.data.customerActivate.customer });
		return dispatch(getRestrictedContent('intranet'));
	});
};

export const resetPassword = () => (dispatch, getState) => {
	const { customerId = false, resetToken = false } = getState().location.payload;
	const { passwordNewRepeat } = getState().checkout.payment.address;

	const resetVariables = {
		id: `gid://shopify/Customer/${customerId}`,
		input: {
			resetToken,
			password: passwordNewRepeat,
		},
	};

	return fetchCheckout('accountRemember', 'account', resetVariables).then((response) => {
		dispatch({ type: UPDATE_ACCESS_TOKEN, data: response.data.customerReset.customerAccessToken });
		dispatch({ type: UPDATE_CUSTOMER_DATA, data: response.data.customerReset.customer });
		return dispatch(getRestrictedContent('intranet'));
	});
};

export const login = () => (dispatch, getState) => {
	const { password, email } = getState().checkout.payment.address;

	const variables = {
		input: {
			email,
			password,
		},
	};

	return fetchCheckout('accountLogin', 'account', variables).then((response) => {
		if (response?.data?.customerAccessTokenCreate?.customerUserErrors && response?.data?.customerAccessTokenCreate?.customerUserErrors.length > 0) {
			dispatch({ type: ACCOUNT_LOGIN_ERROR, data: response.data.customerAccessTokenCreate.customerUserErrors });
			return new Promise((resolve, reject) => reject());
		}
		return dispatch({ type: UPDATE_ACCESS_TOKEN, data: response?.data?.customerAccessTokenCreate?.customerAccessToken });
	}).then(({ data }) => (
		fetchCheckout('accountCustomer', 'account', { accessToken: data.accessToken }).then((response) => {
			dispatch({ type: UPDATE_CUSTOMER_DATA, data: response.data.customer });
			return dispatch(getRestrictedContent('intranet'));
		})
	)).catch(() => {
		console.log('DEBUG: Error retrieving login');
	});
};

export const register = () => (dispatch, getState) => {
	const { payment, selection } = getState().checkout;
	const { setup } = getState().environment;
	const { token } = selection;
	const cleanData = removeInvalidPaymentFields(setup, payment, false, 'selection', true);
	if (cleanData.address.company) delete cleanData.address.company;

	return (
		fetchCheckout('/register', token, { ...cleanData.address }, 'POST').then((accountData) => {
			if (accountData.errors) {
				if (accountData.errors.email) return dispatch({ type: 'SELECTION_ERROR_REGISTERED', hasError: true });
				return dispatch({ type: 'ACCOUNT_ENTRY_FAILED', hasError: true });
			}
			return dispatch({ type: 'UPDATE_ACCOUNT', data: accountData });
		}).catch(() => dispatch({ type: 'ACCOUNT_ENTRY_FAILED', hasError: true }))
	);
};

export const accountOrders = () => (dispatch, getState) => {
	const { selection, account } = getState().checkout;
	const { token } = selection;
	const { orders = [] } = account;

	return fetchCheckout('/orders', token, { size: 1, from: orders.length }, 'POST').then((data) => {
		dispatch({ type: 'UPDATE_ACCOUNT', data });
		return dispatch({ type: 'ACCOUNT_ORDERS', data });
	}).catch((err) => dispatch({ type: 'ACCOUNT_ORDERS_FAILED', err }));
};

export const updateAccountAddress = () => (dispatch, getState) => {
	const { selection, payment } = getState().checkout;
	const { setup } = getState().environment;
	const { token } = selection;
	const cleanData = removeInvalidPaymentFields(setup, payment, false, 'selection', true);
	const args = {};
	const errors = { address: {} };
	const requiredFields = [
		'firstName',
		'lastName',
		'address1',
		'zipCode',
		'city',
		'phoneNumber',
		'country',
	];
	if (cleanData.address.company) delete cleanData.address.company;
	if (cleanData.address.birthday) {
		cleanData.address.customerClubFields = { birthday: cleanData.address.birthday };
		delete cleanData.address.birthday;
	}


	Object.keys(cleanData.address).forEach((input) => {
		const value = cleanData.address[input];
		const checkStates = input === 'state' && setup.countries[cleanData.address.country].states;
		if (checkStates && !checkStates[value]) errors.address.state = 1;
		if (requiredFields.indexOf(input) !== -1 && fieldValidation(value, input)) errors.address[input] = 1;
		if (input.indexOf('password') === -1 && input.indexOf('email') === -1) {
			args[input] = value;
		}
	});

	if (!deepCompare(errors, { address: {} })) {
		dispatch({ type: 'FORM_ERRORS', errors });
		return new Promise((resolve, reject) => reject());
	}

	return fetchCheckout('/customer/update', token, args, 'PUT').then((data) => (
		dispatch({ type: 'UPDATE_ACCOUNT', data })
	)).catch(() => dispatch({ type: 'UPDATE_ACCOUNT_FAILED' }));
};

export const updateAccountEmail = () => (dispatch, getState) => {
	const { token } = getState().checkout.selection;
	const { emailNew, emailNewRepeat } = getState().checkout.payment.address;
	const errors = { address: {} };

	if (fieldValidation(emailNew, 'email')) errors.address.emailNew = 1;
	if (emailNew !== emailNewRepeat) errors.address.emailNewRepeat = 1;

	if (!deepCompare(errors, { address: {} })) {
		return dispatch({ type: 'FORM_ERRORS', errors });
	}

	return fetchCheckout('/email', token, { newEmail: emailNew }, 'PUT').then((data) => {
		if (data.errors && data.errors.newEmail) return dispatch({ type: 'SELECTION_ERROR_REGISTERED', hasError: true });
		return dispatch({ type: 'UPDATE_ACCOUNT', data });
	}).catch(() => dispatch({ type: 'UPDATE_ACCOUNT_FAILED' }));
};

export const updateAccountPassword = () => (dispatch, getState) => {
	const { token } = getState().checkout.selection;
	const { passwordNew, passwordNewRepeat, passwordOld } = getState().checkout.payment.address;
	const errors = { address: {} };

	if (fieldValidation(passwordOld, 'password')) errors.address.passwordOld = 1;
	if (fieldValidation(passwordNew, 'password')) errors.address.passwordNew = 1;
	if (passwordNew !== passwordNewRepeat) errors.address.passwordNewRepeat = 1;

	if (!deepCompare(errors, { address: {} })) {
		dispatch({ type: 'FORM_ERRORS', errors });
		return new Promise((resolve, reject) => reject());
	}

	return fetchCheckout('/password', token, { password: passwordOld, newPassword: passwordNew }, 'PUT').then((data) => {
		if (data.errors && data.errors.password) return dispatch({ type: 'SELECTION_ERROR_PASSWORD', hasError: true });
		return dispatch({ type: 'UPDATE_ACCOUNT', data });
	}).catch(() => dispatch({ type: 'UPDATE_ACCOUNT_FAILED' }));
};

export const logoutAccount = () => (dispatch, getState) => {
	const { accessToken } = getState().checkout.account;

	return fetchCheckout('accountLogout', 'account', { customerAccessToken: accessToken }).then(() => (
		dispatch({ type: ACCOUNT_LOGOUT })
	)).catch(() => dispatch({ type: 'ACCOUNT_LOGOUT_FAILED' }));
};
